import { FloorBasedDesignState } from '@visualizer/types';
import Grid from 'Geodata/grid';
import { SensorData } from 'Simulations/SensorPoint';

type FloorBasedHeightPatch = Pick<FloorBasedDesignState, 'buildingHeight' | 'floorHeight' | 'groundFloorHeight'>;

const computeClampedFloorHeightForFloorCount = (
    span: number,
    floorCount: number,
    thickness: number,
    minHeight: number,
    maxHeight: number
): number => {
    const exactHeight = (span - (floorCount + 1) * thickness) / floorCount;
    return Math.min(maxHeight, Math.max(minHeight, exactHeight));
};

/**
 * Computes the initial floor/ground-floor heights so the generated floor stack
 * fills the current building height (max height) as closely as possible.
 */
export const computeInitialFloorBasedHeightFit = (
    floorBased: FloorBasedDesignState,
    sensors: SensorData[]
): FloorBasedHeightPatch | null => {
    if (sensors.length === 0) return null;

    // 1) Rebuild the same [minZ, maxZ] model used by FloorBased rendering:
    const sensorMinZ = Math.min(...sensors.map((sensor) => sensor.getPosition().z));
    const sensorMaxZ = Math.max(...sensors.map((sensor) => sensor.getPosition().z));
    const minZ = sensorMinZ - Grid.gridRes / 2;
    const thickness = floorBased.thickness.value;
    const maxZ = sensorMaxZ + Grid.gridRes / 2 + thickness;
    const span = maxZ - minZ;
    if (span <= 0) return null;

    // 2) Keep defaults inside both floor and ground-floor constraints:
    const floorMin = Math.max(floorBased.floorHeight.minValue, Grid.gridRes);
    const floorMax = floorBased.floorHeight.maxValue;
    const groundMin = Math.max(floorBased.groundFloorHeight.minValue, Grid.gridRes);
    const groundMax = floorBased.groundFloorHeight.maxValue;
    const sharedMin = Math.max(floorMin, groundMin);
    const sharedMax = Math.min(floorMax, groundMax);
    if (sharedMax < sharedMin) return null;

    // 3) Choose a stable floor count near the preferred height, then clamp the fitted height:
    const preferredHeight = Math.min(sharedMax, Math.max(sharedMin, floorBased.floorHeight.value));
    const preferredFloorCount = Math.max(1, Math.round((span - 2 * thickness) / (preferredHeight + thickness)));
    const minFloorCount = Math.max(1, Math.ceil((span - thickness) / (sharedMax + thickness)));
    const maxFloorCount = Math.max(1, Math.floor((span - thickness) / (sharedMin + thickness)));

    let floorCount = preferredFloorCount;
    if (minFloorCount <= maxFloorCount) {
        floorCount = Math.min(maxFloorCount, Math.max(minFloorCount, preferredFloorCount));
    } else if (preferredFloorCount < minFloorCount) {
        floorCount = minFloorCount;
    } else {
        floorCount = maxFloorCount;
    }

    const fittedHeight = computeClampedFloorHeightForFloorCount(span, floorCount, thickness, sharedMin, sharedMax);

    return {
        buildingHeight: { ...floorBased.buildingHeight, value: maxZ, maxValue: maxZ },
        floorHeight: { ...floorBased.floorHeight, value: fittedHeight },
        groundFloorHeight: { ...floorBased.groundFloorHeight, value: fittedHeight },
    };
};
